<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useAppStore, type Popup } from '@/stores/app';
import { useGtfsStore } from '@/stores/gtfs';
import BaseIcon from '@/components/ui/BaseIcon.vue';

const appStore = useAppStore();
const gtfsStore = useGtfsStore();

const timeoutId = ref<number>();

const popup = computed<Popup>(() => {
  return appStore.popup;
});

watch(
  popup,
  () => {
    setTimeOut();
  },
  { deep: true },
);

function dismiss() {
  appStore.hidePopup();
}

function reloadGtfs() {
  appStore.hidePopup();
  gtfsStore.loadCurrentGtfsFromAPI();
}

function setTimeOut() {
  window.clearTimeout(timeoutId.value);
  if (popup.value.shown) {
    if ((!popup.value.dismissible && !popup.value.reloadGtfsButton) || popup.value.timeout) {
      const timeOut = popup.value.timeout || 3000;
      timeoutId.value = window.setTimeout(() => {
        appStore.hidePopup();
      }, timeOut);
    }
  }
}
</script>

<template>
  <div class="popup-container">
    <div class="popup" :class="{ shown: popup.shown }">
      <BaseIcon v-if="popup.icon" :name="popup.icon" class="popup-icon" />
      <span>{{ popup.text }}</span>
      <button
        v-if="popup.dismissible"
        type="button"
        class="popup-button"
        :aria-label="$t('common.close')"
        @click="dismiss"
      >
        <BaseIcon name="x" />
      </button>
      <button
        v-if="popup.reloadGtfsButton"
        type="button"
        class="popup-button"
        :aria-label="$t('common.retry')"
        @click="reloadGtfs"
      >
        <BaseIcon name="rotate-ccw" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.popup-container {
  left: $spacing-3;
  margin-inline: auto;
  pointer-events: none;
  position: fixed;
  right: $spacing-3;
  text-align: center;
  top: calc($spacing-4 + var(--status-bar-band, env(safe-area-inset-top)));
  width: fit-content;
  z-index: 20;
}

.popup {
  align-items: center;
  background-color: $color-neutral-highest;
  border-radius: $radius-pill;
  box-shadow: $shadow-soft;
  color: $color-white;
  display: inline-flex;
  font: $label-small;
  gap: $spacing-2;
  letter-spacing: $label-small-spacing;
  opacity: 0;
  padding: $spacing-3 $spacing-5;
  transition:
    opacity 0.2s,
    translate 0.2s,
    visibility 0s 0.2s;
  translate: 0 -8px;
  // Once faded out, the hidden buttons must stop catching taps and focus: they
  // sit over the home search bar.
  visibility: hidden;
}

.popup.shown {
  opacity: 1;
  transition:
    opacity 0.2s,
    translate 0.2s;
  translate: 0;
  visibility: visible;
}

.popup-icon {
  flex: none;
  font-size: 20px;
}

.popup-button {
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  /* BaseIcon sizes on 1em: without this the icons would fall back to the label
     text size instead of the 20px the toast icons use. */
  font-size: 20px;
  padding: 0;
  pointer-events: all;
}
</style>
